import React, { useCallback, useEffect, useMemo, useRef } from "react";

import classnames from "classnames";
import "easymde/dist/easymde.min.css";
import { get, useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import SimpleMDEEditor from "./SimpleMDEEditor";

import type EasyMDE from "easymde";
import type { Options } from "easymde";
import type { JSX } from "react";

interface MarkdownEditorFieldProps {
  name: string;
  label?: string;
  sublabel?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  isFullWidth?: boolean;
  isTooltipErrorMessage?: boolean;
}

const baseMdeOptions: Options = {
  spellChecker: false,
  status: false,
  hideIcons: ["guide", "fullscreen", "side-by-side"] as Options["hideIcons"],
};

export default function MarkdownEditorField({
  name,
  label,
  sublabel,
  isRequired = false,
  isReadOnly = false,
  isFullWidth,
  isTooltipErrorMessage,
}: MarkdownEditorFieldProps): JSX.Element {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation("editor-errors");

  const { field } = useController({
    name,
    control,
    rules: {
      required: isRequired && t("requiredField"),
    },
  });

  const error = get(errors, name);

  const mdeInstanceRef = useRef<EasyMDE | null>(null);
  const getMdeInstance = useCallback((mde: EasyMDE) => {
    mdeInstanceRef.current = mde;
  }, []);

  // CodeMirror doesn't render correctly when initialized inside a `display: none`
  // container (e.g. inactive master-media child tab). Refresh when it becomes visible.
  useEffect(() => {
    const wrapper = mdeInstanceRef.current?.codemirror.getWrapperElement();
    if (!wrapper) return undefined;

    const observer = new ResizeObserver(() => {
      mdeInstanceRef.current?.codemirror.refresh();
    });

    observer.observe(wrapper);
    return (): void => observer.disconnect();
  }, []);

  const options = useMemo<Options>(
    () => ({
      ...baseMdeOptions,
      ...(isReadOnly ? { readOnly: true, disableInput: true } : {}),
    }),
    [isReadOnly],
  );

  const containerClassnames = classnames("relative grow basis-2/3", {
    "[&_.CodeMirror.CodeMirror-wrap]:border-destructive!": !!error,
  });

  return (
    <FieldContainer isFullWidth={isFullWidth}>
      {label ? (
        <Label
          isFullWidth={isFullWidth}
          isRequired={isRequired}
          label={label}
          name={name}
          sublabel={sublabel}
        />
      ) : null}
      <div className={containerClassnames}>
        <SimpleMDEEditor
          getMdeInstance={getMdeInstance}
          onChange={field.onChange}
          options={options}
          value={field.value ?? ""}
        />
        <ErrorMessage
          error={error}
          isTooltip={isTooltipErrorMessage}
          name={name}
        />
      </div>
    </FieldContainer>
  );
}
